package com.crowpay.views.components.project.work.scope

import com.crowpay.HasDisplayName
import com.crowpay.ScopeView
import com.crowpay.views.components.nav.AppDimensions
import com.crowpay.edit
import com.crowpay.pending
import com.crowpay.sdk.notFoundError
import com.crowpay.sdk.notNullSession
import com.crowpay.trash
import com.crowpay.utils.mobileButtonRow
import com.crowpay.utils.renderPercent
import com.crowpay.utils.resize
import com.crowpay.views.components.keyMoney
import com.crowpay.views.components.keyString
import com.crowpay.views.components.keyView
import com.crowpay.views.components.label2
import com.crowpay.views.components.labeledSection
import com.crowpay.views.components.priceWithChange
import com.crowpay.views.components.renderPriceChange
import com.crowpay.views.components.scopeSetIcon
import com.crowpay.views.dialogs.EditScopeViewDialog
import com.crowpay.views.dialogs.GenericConfirmationDialog
import com.crowpay.views.screens.common.AuthorizedProjectView
import com.crowpay.views.screens.common.HasProjectData
import com.crowpay.views.screens.common.ProjectLens
import com.crowpay.views.screens.common.assertViewerLens
import com.crowpay.views.screens.contractor.MightBeContractorPage
import com.crowpay.views.theming.DangerButtonSemantic
import com.crowpay.views.theming.InnerTabBar
import com.crowpay.views.theming.LineItemStateSemantic
import com.crowpay.views.theming.ListSemantic
import com.crowpay.views.theming.body
import com.crowpay.views.theming.bodyBold
import com.crowpay.views.theming.darkSection
import com.crowpay.views.theming.lightSection
import com.crowpay.views.theming.secondaryButton
import com.crowpay.views.theming.title
import com.lightningkite.UUID
import com.lightningkite.kiteui.Routable
import com.lightningkite.kiteui.models.Icon
import com.lightningkite.kiteui.models.rem
import com.lightningkite.kiteui.models.times
import com.lightningkite.kiteui.navigation.dialogScreenNavigator
import com.lightningkite.kiteui.reactive.Property
import com.lightningkite.kiteui.reactive.Readable
import com.lightningkite.kiteui.reactive.bind
import com.lightningkite.kiteui.reactive.equalTo
import com.lightningkite.kiteui.reactive.invoke
import com.lightningkite.kiteui.reactive.lens
import com.lightningkite.kiteui.reactive.reactive
import com.lightningkite.kiteui.reactive.shared
import com.lightningkite.kiteui.reactive.waitForNotNull
import com.lightningkite.kiteui.views.ViewModifiable
import com.lightningkite.kiteui.views.ViewWriter
import com.lightningkite.kiteui.views.buttonTheme
import com.lightningkite.kiteui.views.centered
import com.lightningkite.kiteui.views.compact
import com.lightningkite.kiteui.views.direct.button
import com.lightningkite.kiteui.views.direct.col
import com.lightningkite.kiteui.views.direct.onClick
import com.lightningkite.kiteui.views.direct.onlyWhen
import com.lightningkite.kiteui.views.direct.radioToggleButton
import com.lightningkite.kiteui.views.direct.row
import com.lightningkite.kiteui.views.direct.scrolls
import com.lightningkite.kiteui.views.direct.stack
import com.lightningkite.kiteui.views.dynamicTheme
import com.lightningkite.kiteui.views.expanding
import com.lightningkite.kiteui.views.forEachUpdating
import com.lightningkite.kiteui.views.l2.icon
import kotlinx.coroutines.launch

@Routable("project/{projectId}/scope/{scopeId}/{lens}")
class ScopeViewDetails(
    override val projectId: UUID,
    val scopeId: UUID,
    override val lens: ProjectLens,
    parent: HasProjectData? = null
) : MightBeContractorPage, HasProjectData by (parent ?: AuthorizedProjectView.Data(lens, projectId)) {
    val scope = scopes.lens { it.find { s -> s._id == scopeId } ?: notFoundError<ScopeView>() }
    val scopeSet = scope.lens { it.scopeSet }
    val unit = scope.lens { if (it.scopeSet) "Set" else "View" }

    override val title: Readable<String> = shared { scope().scopeTitle }

    val scopeItems = lineItems.lens { it.filter { item -> item.scopeView == scopeId } }

    val status = shared {
        if (scopeSet()) scopeItems().firstOrNull()?.state else null
    }

    val hasPendingDraw = shared {
        val ids = scopeItems().map { it._id }
        payAppItems().any { it.pending && it.lineItem in ids }
    }

    val publishedChanges = shared { changeOrders().filter { it.published != null }.map { it._id } }
    val hasPendingChange = shared {
        val ids = scopeItems().map { it._id }
        val published = publishedChanges()
        pendingItemChanges().any { it.pending && it.itemId in ids && it.changeRequest in published }
    }

    val paid = shared {
        val lines = scopeItems()
        val draws = payAppItems()
        lines.sumOf { line ->
            if (line.complete != null) line.price
            else draws
                .filter { it.lineItem == line._id && (it.pending || it.accepted == true) }
                .sumOf { it.amount }
        }
    }

    enum class Section(override val displayName: String) : HasDisplayName { Info("Info"), Items("Items") }

    val selectedSection = Property(Section.Items)

    override fun ViewWriter.renderMainContent(): ViewModifiable = col {
        launch { assertViewerLens(lens) }

        expanding - scrolls - col {
            spacing = AppDimensions.majorColumnSpacing

            col {
                spacing = 0.5.rem
                lightSection - col {
                    compact - row {
                        centered - onlyWhen { scopeSet() } - scopeSetIcon(AppDimensions.titleTextSize * 1.1)
                        centered - title { ::content { scope().scopeTitle } }
                    }

                    onlyWhen { status() != null } - label2("Status") {
                        title {
                            dynamicTheme {
                                status()?.let(::LineItemStateSemantic)
                            }
                            ::content { status()?.displayName ?: "" }
                        }
                    }
                }
            }

            val example = scopeItems.lens { it.firstOrNull() }.waitForNotNull
            lineItemBanners(
                example,
                shared { val e = example()._id; lineItemCompletionMessages().filter { it.lineItem == e } },
                hasPendingDraw,
                hasPendingChange,
                lens,
                scope
            )

            InnerTabBar.onNext - row {
                for (section in Section.entries) {
                    expanding - buttonTheme - radioToggleButton {
                        centered - body(section.displayName)
                        checked bind selectedSection.equalTo(section)
                    }
                }
            }

            stack {
                reactive {
                    clearChildren()
                    when (selectedSection()) {
                        Section.Info -> infoSection()
                        Section.Items -> itemsSection()
                    }
                }
            }
        }

        actionBar()
    }

    private fun ViewWriter.actionBar(): ViewModifiable {
        val actions = scopeSetActions(this, lens, scope, scopeItems)

        return onlyWhen(false) { scopeSet() && actions.any { it() != null } } - darkSection - mobileButtonRow(actions.reversed())
    }

    private fun ViewWriter.infoSection() = col {
        spacing = AppDimensions.majorColumnSpacing

        onlyWhen { !scope().description.isNullOrBlank() } - labeledSection("Description") {
            body { ::content { scope().description ?: "" } }
        }

        col {
            spacing = AppDimensions.labelSpacing
            bodyBold {
                ::content { "Work ${unit()} Summary" }
            }
            lightSection - col {
                keyMoney("Original Price") { scopeItems().sumOf { it.wraps.originalPrice } }
                keyView("Accepted Changes") {
                    renderPriceChange(false) { scopeItems().sumOf { it.price - it.wraps.originalPrice } }
                }
                keyView("Active Price") {
                    priceWithChange(
                        addPadding = false,
                        price = { scopeItems().sumOf { it.price } },
                        change = {
                            scopeItems().sumOf { it.price - it.wraps.originalPrice }
                        }
                    )
                }
                keyMoney("Paid") { paid() }
                keyString("% Complete") { (paid().toDouble()/scopeItems().sumOf { it.price }).renderPercent() }
            }
        }


        if (lens == ProjectLens.Contractor) row {
            expanding - secondaryButton - button {
                centered - row {
                    centered - icon(Icon.trash.resize(1.2.rem), "delete")
                    centered - body("Delete Scope")
                }
                onClick("Delete Scope") {
                    dialogScreenNavigator.navigate(
                        GenericConfirmationDialog(
                            header = "Delete Scope ${unit()}?",
                            message = "You are about to delete this scope ${unit().lowercase()}. This action cannot be undone. Are you sure?",
                            confirmationText = "Delete Scope",
                            confirmationTheme = DangerButtonSemantic,
                            onSubmit = {
                                if (it) {
                                    val session = notNullSession()
                                    session.scopeViews[scope()._id].delete()
                                }
                            }
                        )
                    )
                }
            }
            expanding - secondaryButton - button {
                centered - row {
                    centered - icon(Icon.edit.resize(1.2.rem), "edit")
                    centered - body("Edit Scope")
                }
                onClick("Edit Scope") {
                    dialogScreenNavigator.navigate(
                        EditScopeViewDialog(
                            options = lineItems(),
                            scope = scope()
                        )
                    )
                }
            }
        }
    }

    private fun ViewWriter.itemsSection() = ListSemantic.onNext - col {
        forEachUpdating(scopeItems) { lightSection - lineItemCard(it, lens, this) }
    }
}